import type { JSX, ReactNode } from 'react';

import { Link, Navigate, useLocation } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '../Alert/Alert';

import { useTenant } from '../../contexts/TenantContext';
import { useIsTenantAdmin } from '../../hooks/useIsTenantAdmin';

import styles from './BillingStatusBanner.module.css';

const BILLING_PATH = '/settings/billing';

/**
 * App-wide notice when this workspace's Revvly payment has failed.
 *
 * Not dismissible: a suspended workspace refuses every change, so a person who
 * closed the notice would meet the refusal with no explanation. Only people who
 * can pay see the link to Billing; everyone else is told whom to ask.
 */
export function BillingStatusBanner(): JSX.Element | null {
  const { selectedTenant } = useTenant();
  const isAdmin = useIsTenantAdmin();
  const status = selectedTenant?.billingStatus;
  if (
    status !== 'PAST_DUE' &&
    status !== 'SUSPENDED' &&
    status !== 'CANCELLED' &&
    status !== 'BLOCKED'
  ) {
    return null;
  }

  const readOnly = status !== 'PAST_DUE';
  const message =
    status === 'PAST_DUE'
      ? 'A payment for this workspace is overdue. Update your card to avoid interruption.'
      : status === 'SUSPENDED'
        ? 'This workspace is read only because a payment is overdue. Pay the open invoice to restore it.'
        : status === 'BLOCKED'
          ? 'Your free trial has ended. Add a card to keep using this workspace; your data is kept.'
          : 'This workspace is read only because its subscription was cancelled.';
  const label =
    status === 'PAST_DUE'
      ? 'Payment overdue.'
      : status === 'BLOCKED'
        ? 'Trial ended.'
        : 'Read only.';

  return (
    <div className={readOnly ? styles.BannerDanger : styles.BannerWarning} role="status">
      <span className={styles.Text}>
        <strong>{label}</strong> {message}
        {!isAdmin && ' Ask a workspace admin.'}
      </span>
      {isAdmin && (
        <Link className={styles.Action} to={BILLING_PATH}>
          Open billing
        </Link>
      )}
    </div>
  );
}

/**
 * A workspace whose trial ended with no card (BLOCKED) can only reach Billing:
 * the API refuses everything else. Admins are taken there; anyone else gets a
 * notice in place of the page, which could not load.
 */
export function BlockedWorkspaceGate({ children }: { children: ReactNode }): JSX.Element {
  const { selectedTenant } = useTenant();
  const isAdmin = useIsTenantAdmin();
  const { pathname } = useLocation();
  if (selectedTenant?.billingStatus !== 'BLOCKED' || pathname.startsWith(BILLING_PATH)) {
    return <>{children}</>;
  }
  if (isAdmin) return <Navigate to={BILLING_PATH} replace />;
  return (
    <div className={styles.Blocked}>
      <Alert variant="warning">
        <AlertTitle>This workspace's trial has ended</AlertTitle>
        <AlertDescription>
          Ask a workspace admin to add a card in Billing. Everything you set up is kept.
        </AlertDescription>
      </Alert>
    </div>
  );
}
